/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useMemo } from 'react';
import { View, Dimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SkeletonLoaderView from '@airasia-phoenix/react-native/components/SkeletonLoaderView';
import { pd } from '@airasia-rn/ui';

// Mirror FeedListWidget's grid so the shimmer matches the real MOVEments layout.
const HORIZONTAL_GAP = 2;
const GRID_PADDING_HORIZONTAL = 16;
const NUM_COLUMNS = 3;
const TILE_HEIGHT = 200;
const CORNER_RADIUS = 16;

export interface MovementsGridSkeletonViewProps {
  /** Number of shimmer tiles to render. Defaults to a full 3x3 grid. */
  count?: number;
}

/**
 * Loading placeholder for the MOVEments feed: a 3-column grid of shimmer tiles
 * that matches FeedListWidget's real grid (unlike the 2-column For You masonry
 * skeleton, which is the wrong shape for MOVEments).
 */
export const MovementsGridSkeletonView = ({
  count = NUM_COLUMNS * 3,
}: MovementsGridSkeletonViewProps) => {
  const screenWidth = Dimensions.get('window').width;

  const itemWidth = useMemo(() => {
    const availableWidth = screenWidth - GRID_PADDING_HORIZONTAL * 2;
    const totalGap = (NUM_COLUMNS - 1) * HORIZONTAL_GAP;
    return Math.floor((availableWidth - totalGap) / NUM_COLUMNS);
  }, [screenWidth]);

  const tiles = useMemo(
    () => Array.from({ length: count }, (_, index) => index),
    [count],
  );

  return (
    <View style={styles.container}>
      {tiles.map((index) => {
        const isFirstRow = index < NUM_COLUMNS;
        const isLastRow =
          index >= tiles.length - (tiles.length % NUM_COLUMNS || NUM_COLUMNS);
        const isFirstColumn = index % NUM_COLUMNS === 0;
        const isLastColumn = index % NUM_COLUMNS === NUM_COLUMNS - 1;

        return (
          <View
            key={index}
            style={[
              styles.tile,
              {
                width: itemWidth,
                marginRight: isLastColumn ? 0 : HORIZONTAL_GAP,
                // Row-gap emulation: RN doesn't collapse trailing margins, so
                // skip marginBottom on the last row to avoid extra space below
                // the grid (this container has no paddingBottom of its own).
                marginBottom: isLastRow ? 0 : HORIZONTAL_GAP,
                borderTopLeftRadius:
                  isFirstRow && isFirstColumn ? CORNER_RADIUS : 0,
                borderTopRightRadius:
                  isFirstRow && isLastColumn ? CORNER_RADIUS : 0,
                borderBottomLeftRadius:
                  isLastRow && isFirstColumn ? CORNER_RADIUS : 0,
                borderBottomRightRadius:
                  (isLastRow && isLastColumn) || index === tiles.length - 1
                    ? CORNER_RADIUS
                    : 0,
              },
            ]}
          >
            <SkeletonLoaderView style={{ flex: 1 }} />
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create((pd) => ({
  container: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    paddingHorizontal: GRID_PADDING_HORIZONTAL,
  },
  tile: {
    height: TILE_HEIGHT,
    // marginBottom is set per-tile inline (skipped on the last row) — see render.
    overflow: 'hidden' as const,
    backgroundColor: pd.sys.colour.container_low.toString(),
  },
}));

export default MovementsGridSkeletonView;
